import {Mesh, MeshAttribute, MeshComponent, Object3D} from '@wonderlandengine/api';
import {Mathf} from '../../utils/Mathf.js';
import {RefObject} from 'react';
import {TextProps} from '@wonderlandengine/react-ui';

export type Color = string | Float32Array | number;
export type Percentage = number | `${number}%`;

function hexToFloat32Array(hex: string, out?: Float32Array): Float32Array {
    // Check if the first character is '#' and remove it if present
    if (hex[0] === '#') {
        hex = hex.slice(1);
    }

    let r: number,
        g: number,
        b: number,
        a: number = 1.0;

    if (hex.length === 3) {
        // #fff format
        r = parseInt(hex[0] + hex[0], 16) / 255;
        g = parseInt(hex[1] + hex[1], 16) / 255;
        b = parseInt(hex[2] + hex[2], 16) / 255;
    } else if (hex.length === 6) {
        // #ffffff format
        r = parseInt(hex.slice(0, 2), 16) / 255;
        g = parseInt(hex.slice(2, 4), 16) / 255;
        b = parseInt(hex.slice(4, 6), 16) / 255;
    } else if (hex.length === 8) {
        // #ff00ff00 format
        r = parseInt(hex.slice(0, 2), 16) / 255;
        g = parseInt(hex.slice(2, 4), 16) / 255;
        b = parseInt(hex.slice(4, 6), 16) / 255;
        a = parseInt(hex.slice(6, 8), 16) / 255;
    } else {
        throw new Error('Invalid hex color format');
    }

    out = out ?? new Float32Array([r, g, b, a]);
    out[0] = r;
    out[1] = g;
    out[2] = b;
    out[3] = a;
    return out;
}

export function parseColor(
    hex: number | string | Float32Array,
    out?: Float32Array
): Float32Array {
    if (typeof hex == 'string') return hexToFloat32Array(hex, out);
    if (typeof hex == 'number') {
        out = out ?? new Float32Array(4);
        out[0] = ((hex >> 24) & 0xff) / 255;
        out[1] = ((hex >> 16) & 0xff) / 255;
        out[2] = ((hex >> 8) & 0xff) / 255;
        out[3] = (hex & 0xff) / 255;
        return out;
    }
    return hex;
}

export function formatString(template: string, ...values: any[]) {
    return template.replace(/{(\d+)}/g, (match, index) => {
        return typeof values[index] !== 'undefined' ? values[index] : match;
    });
}

export function insertNewlines(text: string, maxLineLength: number): string {
    const words = text.split(' ');
    let result = '';
    let currentLine = '';

    for (const word of words) {
        if ((currentLine + word).length > maxLineLength) {
            result += currentLine.trim() + '\n';
            currentLine = '';
        }
        currentLine += word + ' ';
    }

    result += currentLine.trim(); // Add any remaining words

    return result;
}

/**
 * Changes the alpha value of a color
 * @param color The color to set the alpha of
 * @param alpha The alpha value to set. Can be a number between 0 and 1 or a percentage string
 * @returns the color with the new alpha value
 */
export function setAlpha(color: Color, alpha: Percentage): Color {
    let alphaValue: number;

    if (typeof alpha === 'number') {
        alphaValue = alpha;
    }
    if ((alpha as string).endsWith('%')) {
        alphaValue = parseFloat(alpha as string) / 100;
    }

    alphaValue = Mathf.clamp(alphaValue, 0, 1);

    if (typeof color == 'string') {
        return hexToFloat32Array(color).map((c, i) => (i == 3 ? alphaValue : c));
    }

    if (color instanceof Float32Array) {
        color[3] = alphaValue;
        return color;
    }
    return color;
}

/**
 * returns a fontsize based on the length of the text, the provided width and the font size
 * @param text
 */
export function resizeLabelToFit(text: string, width: number, fontSize: number): number {
    const len = text.length;
    // calculate the width of the text
    const textWidth = len * fontSize;

    if (textWidth < width) {
        return fontSize;
    }

    // calculate the new font size
    fontSize = Math.floor((width / textWidth) * fontSize);
    return fontSize;
}
